import { ApolloClient, ApolloLink, InMemoryCache, HttpLink, gql } from "@apollo/client";
import { Defer20220824Handler } from "@apollo/client/incremental";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { onError, type ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { Observable } from "@apollo/client/utilities";
import { toast } from "@/components/feedback/toast";
import { sanitizeErrorMessage } from "@shared/sanitize";
import { getProviderAuthAdapter } from "@/lib/providerRegistry";
import { recordAuthTrace } from "@/lib/authTrace";
import {
  hasClientCredentialFailure,
  observeClientCredentialFailure,
  observeClientCredentialRecovery,
} from "@/lib/clientCredentialObservations";
import { OPERATION_KIND_HEADER } from "@shared/operationIdentity";
import { clientLoadTimelineNow, markClientGraphqlOperation } from "@/lib/clientLoadTimeline";
import {
  CATEGORY_MESSAGES,
  isErrorCategory,
  providerImpactForErrorCategory,
  type ErrorCategory,
  type ProviderAccessState,
  type ProviderAvailability,
} from "@shared/errorCategories";

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL || "/api/graphql",
  credentials: "include",
});

// ── Types ────────────────────────────────────────────────────────────
type AuthFailureReason = "refresh_failed" | "session_not_found" | "provider_unresolved";

export type AuthFailureEvent = {
  provider: string;
  reason: AuthFailureReason;
  statusCode?: number;
  connectionId: string;
};

export type ApolloErrorKind = "auth" | "network" | "rate_limit" | "server" | "graphql";

export type NormalizedApolloError = {
  kind: ApolloErrorKind;
  message: string;
  dedupeKey: string;
  statusCode?: number;
  category?: ErrorCategory;
  providerImpact?: {
    access?: ProviderAccessState;
    availability?: ProviderAvailability;
  };
};

function normalizeProvider(provider: string | null | undefined): string | null {
  return provider?.trim() || null;
}

// ── Active connection ─────────────────────────────────────────────────
// This module holds NO connection identity of its own. Operations self-identify
// via the viewKey variable (authLink below); the error link attributes failures
// via the operation's own headers plus the normalized Connection entities in the
// Apollo cache. Storage seeds for resolution live in lib/connectionSeed.ts and
// are owned by the connection store — never read on the transport path.

function getHeaderValue(headers: unknown, name: string): string | null {
  if (!headers) return null;
  if (headers instanceof Headers) return headers.get(name);
  if (typeof headers !== "object") return null;
  const record = headers as Record<string, unknown>;
  const direct = record[name] ?? record[name.toLowerCase()];
  return typeof direct === "string" && direct.trim() ? direct : null;
}

function withoutHeader(headers: unknown, name: string): Record<string, unknown> {
  if (headers instanceof Headers) {
    return Object.fromEntries(
      Array.from(headers.entries()).filter(([key]) => key.toLowerCase() !== name.toLowerCase())
    );
  }
  if (!headers || typeof headers !== "object") return {};
  return Object.fromEntries(
    Object.entries(headers as Record<string, unknown>).filter(
      ([key]) => key.toLowerCase() !== name.toLowerCase()
    )
  );
}

// ── Auth link: per-operation connection-id routing ───────────────────
// Connection-scoped operations self-identify via a client-only `viewKey`
// variable (a connectionId, or "fixtures"): the cache partitions on it (see
// viewScopedKeyArgs) and this link derives the transport headers from it, so
// an operation's identity is bound when the operation is created — never read
// from ambient storage at request time. HttpLink strips variables the query
// document doesn't declare, so viewKey never reaches the server.
// context.connectionId remains as an operation-bound override for imperative
// callers; account/fixture operations declare context.operationKind directly.
// Every request declares its operation kind. Connector identity is never
// removed as a failure signal: a failed B operation either remains attributable
// to B or is stopped by its caller, and can never fall back to login connection A.
export function extractViewKey(variables: Record<string, unknown> | undefined): string | null {
  const raw = variables?.viewKey;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

const authLink = setContext(
  (request, { headers, operationKind, connectionId: contextConnectionId, dataSource, ...rest }) => {
    if (operationKind === "account") {
      return {
        ...rest,
        headers: {
          ...withoutHeader(headers, "x-connection-id"),
          [OPERATION_KIND_HEADER]: "account",
        },
      };
    }
    const viewKey = extractViewKey(request.variables as Record<string, unknown> | undefined);
    const operationConnectionId =
      typeof contextConnectionId === "string" && contextConnectionId.trim()
        ? contextConnectionId.trim()
        : null;

    // Fixture provider: add x-data-source header
    if (
      operationKind === "fixture" ||
      viewKey === "fixtures" ||
      dataSource === "fixtures" ||
      getHeaderValue(headers, "x-data-source") === "fixtures"
    ) {
      return {
        ...rest,
        headers: {
          ...withoutHeader(headers, "x-connection-id"),
          "x-data-source": "fixtures",
          [OPERATION_KIND_HEADER]: "fixture",
        },
      };
    }

    const connectionId = viewKey ?? operationConnectionId;
    return connectionId
      ? {
          ...rest,
          headers: {
            ...headers,
            "x-connection-id": connectionId,
            [OPERATION_KIND_HEADER]: "connector",
          },
        }
      : {
          ...rest,
          headers: { ...headers, [OPERATION_KIND_HEADER]: "connector" },
        };
  }
);

// ── Cache keying helpers ─────────────────────────────────────────────
// Partition connection-scoped root fields by the operation's own viewKey
// variable. The key is bound to the operation, so a response that lands after
// the user switches connections is still filed under the connection that
// issued it (the old global-read version raced exactly there). Operations
// that omit viewKey share a legacy "unscoped" bucket — and go out with no
// x-connection-id header, so protected fields fail loudly.
function viewScopedKeyArgs(argNames: string[]) {
  return (
    args: Record<string, unknown> | null,
    ctx: { fieldName: string; variables?: Record<string, unknown> }
  ): string => {
    const scope = extractViewKey(ctx.variables) ?? "unscoped";
    const argFragment = argNames.map((n) => `${n}=${JSON.stringify(args?.[n] ?? null)}`).join(",");
    return `${ctx.fieldName}:view=${scope}(${argFragment})`;
  };
}

// ── Token refresh ────────────────────────────────────────────────────
// Module-level promise to deduplicate concurrent refresh attempts (prevents
// multiple refresh calls when multiple operations fail simultaneously with 401).
// Keyed by immutable connection identity so two connections for one provider
// never borrow each other's refresh work.
const refreshPromises = new Map<string, Promise<void>>();

// ── Proactive refresh scheduler ──────────────────────────────────────
// Schedules a background refresh at ~80% of the access token lifetime so the
// token is silently renewed well before it expires.
const refreshTimers = new Map<string, ReturnType<typeof setTimeout>>();

function getGraphQLErrorCode(error: CombinedGraphQLErrors): string | null {
  for (const entry of error.errors) {
    const code = entry.extensions?.code;
    if (typeof code === "string" && code.length > 0) {
      return code;
    }
  }
  return null;
}

function getGraphQLErrorCategory(error: CombinedGraphQLErrors): ErrorCategory | null {
  const categories = new Set(
    error.errors
      .map((entry) => entry.extensions?.code)
      .filter((code): code is ErrorCategory => isErrorCategory(code))
  );
  // One response may contain errors from several concurrently resolved fields.
  // Pick one deterministic operational category, preferring actionable
  // connection state and transient availability over an access downgrade.
  for (const category of [
    "UNAUTHENTICATED",
    "RATE_LIMITED",
    "UPSTREAM_UNAVAILABLE",
    "FORBIDDEN",
    "NOT_FOUND",
  ] as const) {
    if (categories.has(category)) return category;
  }
  return null;
}

function providerErrorFields(category: ErrorCategory) {
  return {
    category,
    providerImpact: providerImpactForErrorCategory(category),
  };
}

function isLikelyNetworkError(error: unknown): boolean {
  if (ServerError.is(error)) return false;
  if (CombinedGraphQLErrors.is(error)) return false;
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  return message.includes("network") || message.includes("fetch");
}

export function normalizeApolloError(error: unknown): NormalizedApolloError | null {
  if (!error) return null;

  // Silently discard cancelled requests — AbortError must never trigger retry or toast.
  // Note: DOMException is not instanceof Error in jsdom/some environments, so we check
  // name property directly on any object that has it.
  if (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: unknown }).name === "AbortError"
  )
    return null;
  if (error instanceof Error && error.name === "AbortError") return null;
  if (
    typeof error === "object" &&
    error !== null &&
    "networkError" in error &&
    (error as { networkError: unknown }).networkError !== null &&
    typeof (error as { networkError: unknown }).networkError === "object" &&
    "name" in (error as { networkError: object }).networkError &&
    (error as { networkError: { name: unknown } }).networkError.name === "AbortError"
  )
    return null;

  if (CombinedGraphQLErrors.is(error)) {
    const category = getGraphQLErrorCategory(error);
    const code = category ?? getGraphQLErrorCode(error);

    if (category === "UNAUTHENTICATED") {
      return {
        kind: "auth",
        message: "Your session expired. Sign in again to keep working.",
        dedupeKey: "apollo-auth",
        statusCode: 401,
        ...providerErrorFields(category),
      };
    }

    if (category === "RATE_LIMITED" || code === "TOO_MANY_REQUESTS") {
      return {
        kind: "rate_limit",
        message: "The connected provider is rate limiting requests right now. Try again shortly.",
        dedupeKey: `apollo-rate-limit:${code}`,
        statusCode: 429,
        ...(category ? providerErrorFields(category) : {}),
      };
    }

    if (category === "UPSTREAM_UNAVAILABLE") {
      return {
        kind: "server",
        message: CATEGORY_MESSAGES.UPSTREAM_UNAVAILABLE,
        dedupeKey: "apollo-provider:UPSTREAM_UNAVAILABLE",
        ...providerErrorFields(category),
      };
    }

    return {
      kind: "graphql",
      message: sanitizeErrorMessage(
        error.errors[0]?.message ?? "The request failed before DeployCompare could finish it."
      ),
      dedupeKey: `apollo-graphql:${code ?? "unknown"}`,
      ...(category ? providerErrorFields(category) : {}),
    };
  }

  if (ServerError.is(error)) {
    if (error.statusCode === 401) {
      return {
        kind: "auth",
        message: "Your session expired. Sign in again to keep working.",
        dedupeKey: "apollo-auth",
        statusCode: 401,
      };
    }

    if (error.statusCode === 429) {
      return {
        kind: "rate_limit",
        message: "The connected provider is rate limiting requests right now. Try again shortly.",
        dedupeKey: "apollo-rate-limit:429",
        statusCode: 429,
        ...providerErrorFields("RATE_LIMITED"),
      };
    }

    if (error.statusCode >= 500) {
      return {
        kind: "server",
        message: "The server had trouble finishing that request. Retry in a moment.",
        dedupeKey: `apollo-server:${error.statusCode}`,
        statusCode: error.statusCode,
      };
    }

    return {
      kind: "network",
      message: "The request did not complete successfully. Check the connection and retry.",
      dedupeKey: `apollo-http:${error.statusCode}`,
      statusCode: error.statusCode,
    };
  }

  if (isLikelyNetworkError(error)) {
    return {
      kind: "network",
      message: "DeployCompare could not reach the API. Check your connection and retry.",
      dedupeKey: `apollo-network:${error instanceof Error ? error.message : "unknown"}`,
    };
  }

  return {
    kind: "graphql",
    message: sanitizeErrorMessage(
      error instanceof Error
        ? error.message
        : "The request failed before DeployCompare could finish it."
    ),
    dedupeKey: "apollo-unknown",
  };
}

function reportApolloError(error: unknown): void {
  const normalized = normalizeApolloError(error);
  if (!normalized || normalized.kind === "auth") return;

  const variant = normalized.kind === "rate_limit" ? "warning" : "error";
  toast[variant]({
    title:
      normalized.kind === "rate_limit"
        ? "Request slowed down"
        : normalized.kind === "network"
          ? "Connection problem"
          : normalized.kind === "server"
            ? "Server problem"
            : "Request failed",
    description: normalized.message,
    dedupeKey: normalized.dedupeKey,
  });
}

/**
 * Connector query errors render inside the identity-scoped active view. Keeping
 * transport reporting account-only prevents a provider outage from producing
 * one toast per racing connector query (and from showing a late old-provider
 * toast after a switch).
 */
export function shouldReportApolloError(
  operationKind: string | null,
  normalized: NormalizedApolloError
): boolean {
  return normalized.kind !== "auth" && operationKind !== "connector";
}

export function scheduleTokenRefresh(
  provider: string,
  expiresInSec: number,
  connectionId: string
): void {
  const key = `${provider}:${connectionId}`;
  clearScheduledRefresh(provider, connectionId);
  // Refresh at 80% of token lifetime, but no sooner than 30 seconds
  const delayMs = Math.max(expiresInSec * 0.8 * 1000, 30_000);
  const timer = setTimeout(() => {
    refreshTimers.delete(key);
    handleRefreshOn401(provider, connectionId).catch((error: unknown) => {
      console.error("[apollo] scheduled token refresh failed", { provider, connectionId, error });
    });
  }, delayMs);
  refreshTimers.set(key, timer);
}

export function clearScheduledRefresh(provider: string, connectionId?: string): void {
  const exactKey = connectionId ? `${provider}:${connectionId}` : null;
  for (const [key, timer] of refreshTimers) {
    if (exactKey ? key === exactKey : key.startsWith(`${provider}:`)) {
      clearTimeout(timer);
      refreshTimers.delete(key);
    }
  }
}

export type AuthRecoveryEvent = { provider: string; connectionId: string };

export function notifyAuthRecovery(event: AuthRecoveryEvent): void {
  recordAuthTrace("apollo.auth_recovery", {
    provider: normalizeProvider(event.provider),
    connectionId: true,
  });
  observeClientCredentialRecovery({
    provider: event.provider,
    connectionId: event.connectionId,
    reason: "refresh_succeeded",
  });
}

// Lazy reference to the Apollo client — set after client is created (below).
// Used by notifyAuthFailure to clear cache on permanent auth failure, and by
// getConnectionProviderFromCache to read normalized Connection entities.
type ClientRef = {
  cache?: Pick<InMemoryCache, "identify" | "modify">;
  readFragment?: (options: { id: string; fragment: ReturnType<typeof gql> }) => unknown;
};
let _clientRef: ClientRef | null = null;
export function _setClientRef(c: ClientRef): void {
  _clientRef = c;
}

export function evictConnectionScopedCache(connectionId: string): void {
  const marker = `:view=${connectionId}(`;
  _clientRef?.cache?.modify({
    id: "ROOT_QUERY",
    fields(value, { storeFieldName, DELETE }) {
      return storeFieldName.includes(marker) ? DELETE : value;
    },
  });
}

const CONNECTION_PROVIDER_FRAGMENT = gql`
  fragment ConnectionProviderField on Connection {
    provider
  }
`;

/**
 * A connection's provider, read from the normalized Connection entity the live
 * MY_CONNECTIONS query put in the Apollo cache — the connection list stays the
 * single source; there is no parallel connection→provider map to keep fed.
 * Null before the list has loaded (deliberate: without a provider we cannot
 * safely select a refresh adapter, so the original auth error propagates).
 */
export function getConnectionProviderFromCache(
  connectionId: string | null | undefined
): string | null {
  if (!connectionId || !_clientRef?.cache || !_clientRef.readFragment) return null;
  const cacheId = _clientRef.cache.identify({ __typename: "Connection", id: connectionId });
  if (!cacheId) return null;
  const data = _clientRef.readFragment({
    id: cacheId,
    fragment: CONNECTION_PROVIDER_FRAGMENT,
  }) as { provider?: string } | null;
  return data?.provider ?? null;
}

export function notifyAuthFailure(event: AuthFailureEvent): void {
  const provider = normalizeProvider(event.provider);
  recordAuthTrace("apollo.auth_failure", {
    provider,
    reason: event.reason,
    statusCode: event.statusCode ?? null,
    connectionId: Boolean(event.connectionId),
  });
  observeClientCredentialFailure({
    provider: provider ?? event.provider,
    connectionId: event.connectionId,
    reason: event.reason,
    statusCode: event.statusCode,
  });
  clearScheduledRefresh(event.provider, event.connectionId);
  evictConnectionScopedCache(event.connectionId);
}

/**
 * Attempt a token refresh for the given provider.
 * Deduplicates concurrent calls — only one fetch is made while a refresh is in-flight.
 * Retries twice (immediate + 2s delay) before declaring permanent failure.
 * Schedules the next proactive refresh on success.
 * Notifies the connection store and throws on permanent failure.
 */
export async function handleRefreshOn401(
  provider: string,
  failedConnectionId: string
): Promise<void> {
  if (!failedConnectionId) {
    throw new Error("Connection identity required for token refresh");
  }
  const adapter = getProviderAuthAdapter(provider);
  const refreshAccessToken = adapter?.refreshAccessToken;
  const connectionId = failedConnectionId;
  if (!adapter?.canRefreshAccessToken() || !refreshAccessToken) {
    notifyAuthFailure({
      provider,
      reason: "session_not_found",
      connectionId,
    });
    throw new Error("Server-managed session requires reauthentication");
  }

  const refreshKey = `${provider}:${connectionId}`;
  // Return existing in-flight refresh for this exact connection.
  const existing = refreshPromises.get(refreshKey);
  if (existing) return existing;

  const refreshPromise = (async () => {
    try {
      const delays = [0, 2000]; // immediate first attempt, then 2s backoff
      let lastStatus: number | undefined;

      for (const delay of delays) {
        if (delay > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, delay));
        }

        const result = await refreshAccessToken({
          id: connectionId,
          provider: adapter.provider,
        });

        if (result.refreshed && !result.needs_reauth) {
          notifyAuthRecovery({ provider, connectionId });
          if (typeof result.expires_in === "number" && result.expires_in > 0) {
            scheduleTokenRefresh(provider, result.expires_in, connectionId);
          }
          return;
        }

        lastStatus = 401;
      }

      // All retries exhausted — permanent failure. This is a PROVIDER
      // credential failure (matrix row A3): the app session is still valid,
      // so the copy must ask for a provider reconnect, never a sign-in.
      // Connection state is the rendering authority. A transport-owned toast
      // would duplicate the active view's reconnect status and, worse, allow a
      // late 401 from a provider the user already left to paint over the new
      // provider. Record the exact connection failure and let active projection
      // decide whether anything is visible.
      notifyAuthFailure({
        provider,
        reason: "refresh_failed",
        statusCode: lastStatus,
        connectionId,
      });
      console.warn("[apollo] token refresh failed permanently after retries", {
        provider,
        statusCode: lastStatus,
      });
      throw new Error(`Token refresh failed: ${lastStatus}`);
    } finally {
      refreshPromises.delete(refreshKey);
    }
  })();

  refreshPromises.set(refreshKey, refreshPromise);
  return refreshPromise;
}

// ── Error link: intercept auth errors and retry after refresh ────────
const errorLink = onError(({ error, operation, forward }: ErrorLink.ErrorHandlerOptions) => {
  const normalized = normalizeApolloError(error);

  if (!normalized) return;
  if (normalized.kind !== "auth") {
    const operationKind = getHeaderValue(operation.getContext().headers, OPERATION_KIND_HEADER);
    // Active connector views own their query errors after exact identity
    // scoping. Toasting here would render stale errors from a provider the user
    // has already left and duplicate every racing query's inline status.
    if (shouldReportApolloError(operationKind, normalized)) {
      reportApolloError(error);
    }
    return;
  }

  // Attribute the failure to the operation's OWN connection (the header the
  // auth link derived from its viewKey) — never to ambient state, so a 401
  // that lands after the user switches connections still blames the right one.
  // Headerless operations (account-scoped, fixtures, unscoped) have no
  // connection to refresh: their auth error propagates to the caller as-is.
  const operationHeaders = operation.getContext().headers;
  const failedConnectionId = getHeaderValue(operationHeaders, "x-connection-id");
  if (!failedConnectionId) return;
  const failedProvider = getConnectionProviderFromCache(failedConnectionId);

  // The connection id is already immutable operation identity. A connector
  // query can fail before MY_CONNECTIONS has populated the normalized cache;
  // keep that failure attributed to the exact connection and project the
  // reconnect state, but do not guess which provider refresh adapter to call.
  if (hasClientCredentialFailure(failedConnectionId)) {
    return;
  }
  if (!failedProvider) {
    notifyAuthFailure({
      provider: "unknown",
      reason: "provider_unresolved",
      statusCode: normalized.statusCode ?? 401,
      connectionId: failedConnectionId,
    });
    return;
  }

  return new Observable((observer) => {
    handleRefreshOn401(failedProvider, failedConnectionId)
      .then(() => {
        const subscriber = forward(operation).subscribe(observer);
        return () => subscriber.unsubscribe();
      })
      .catch((refreshError: unknown) => {
        observer.error(refreshError);
      });
  });
});

// ── Retry link: auto-retry transient network / server errors ─────────
// Retries failed HTTP requests (e.g. backend not yet ready after restart)
// with exponential backoff. Auth (401) and rate-limit (429) errors are
// excluded — they are handled by errorLink and should not be retried blindly.
const retryLink = new RetryLink({
  delay: { initial: 1000, max: 5000, jitter: true },
  attempts: {
    max: 5,
    retryIf: (error: { statusCode?: number } & Partial<Error>) => {
      // Auth errors themselves are excluded below. Do not let one connection's
      // permanent auth failure suppress retries for unrelated providers.
      if (error?.statusCode === 401) return false;
      if (error?.statusCode === 429) return false;
      if (error?.statusCode && error.statusCode >= 400 && error.statusCode < 500) return false;
      return true;
    },
  },
});

// Read-path waterfall instrumentation. This link records operation names and
// timings only; variables and response data never enter the diagnostic export.
const clientLoadTimelineLink = new ApolloLink((operation, forward) => {
  const startedAtMs = clientLoadTimelineNow();
  const operationName = operation.operationName || "anonymous";
  let sawResult = false;
  markClientGraphqlOperation(operationName, "start", startedAtMs);
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (value) => {
        if (!sawResult) {
          sawResult = true;
          markClientGraphqlOperation(operationName, "first_result", startedAtMs);
        }
        observer.next(value);
      },
      error: (error: unknown) => {
        markClientGraphqlOperation(operationName, "error", startedAtMs);
        observer.error(error);
      },
      complete: () => {
        markClientGraphqlOperation(operationName, "complete", startedAtMs);
        observer.complete();
      },
    });
    return () => subscription.unsubscribe();
  });
});

// Production typePolicies — exported so tests can build mirror caches that
// exercise the real cache-key wiring (not a re-implemented copy).
// Adding a connection-scoped Query field? Register it here so its results
// are partitioned by the operation's viewKey variable. See viewScopedKeyArgs above.
export const apolloTypePolicies = {
  Project: {
    keyFields: false,
  },
  PipelineSummary: {
    keyFields: false,
  },
  User: {
    keyFields: ["id"],
  },
  Connection: {
    keyFields: ["id"],
  },
  ChangeTotals: {
    // Some pipeline summaries return { add, change, destroy } (3 fields) while
    // others include replace (4 fields). merge: true tells Apollo to merge
    // incoming fields into existing rather than replacing, preventing data loss
    // warnings when the shapes differ.
    merge: true,
  },
  ScoredVariant: {
    keyFields: false,
  },
  Query: {
    fields: {
      comparePreferences: {
        keyArgs: viewScopedKeyArgs(["projectPath"]),
      },
      availableProviders: {
        merge: false,
      },
      me: {
        merge: false,
      },
      myConnections: {
        merge: false,
      },
      projects: {
        keyArgs: viewScopedKeyArgs([]),
        merge: false,
      },
      projectMemory: {
        keyArgs: viewScopedKeyArgs([]),
        merge: false,
      },
      branchPipelines: {
        keyArgs: viewScopedKeyArgs(["projectPath", "branch", "page", "perPage", "allRefs"]),
        merge: false,
      },
      branchPipelineHistory: {
        keyArgs: viewScopedKeyArgs(["projectPath", "branch", "page", "perPage", "allRefs"]),
        merge: false,
      },
      compareJobsProgress: {
        keyArgs: viewScopedKeyArgs(["input"]),
        merge: false,
      },
      parserCatalog: {
        merge: false,
      },
      pipelineTimeline: {
        keyArgs: viewScopedKeyArgs(["projectPath", "limit", "ref"]),
        merge: false,
      },
      projectSourceSelection: {
        keyArgs: viewScopedKeyArgs(["projectPath"]),
        merge: false,
      },
      // Connection-scoped fields threaded with viewKey for transport but previously
      // NOT partitioned in the cache — two same-provider connections collapsed onto one
      // entry, bleeding the wrong connection's refs/usage/webhooks after a switch (no
      // resetStore on switch means cache-key partitioning is the only isolation). P-190.
      availableRefs: {
        keyArgs: viewScopedKeyArgs(["projectPath"]),
        merge: false,
      },
      workspaceUsageStatus: {
        keyArgs: viewScopedKeyArgs(["projectPath"]),
      },
      projectAnalysisStatus: {
        keyArgs: viewScopedKeyArgs(["projectPath"]),
      },
      webhookSettings: {
        keyArgs: viewScopedKeyArgs(["provider"]),
        merge: false,
      },
    },
  },
} as const;

export const client = new ApolloClient({
  link: clientLoadTimelineLink
    .concat(errorLink)
    .concat(authLink)
    .concat(retryLink)
    .concat(httpLink),
  cache: new InMemoryCache({ typePolicies: apolloTypePolicies }),
  incrementalHandler: new Defer20220824Handler(),
  defaultOptions: {
    query: {
      fetchPolicy: "cache-first",
    },
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  },
});

// Wire lazy client ref so notifyAuthFailure can clear the cache.
_setClientRef(client);
